import { useContext, useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import Select from 'react-select';
import { getBeatsApi, getReceiptApi, getUserListApi } from '../api/api.js';
import { AuthContext } from '../context/AuthContext.jsx';
import { useOrganizationSettings } from '../context/OrganizationSettingsContext';
import { useCompany } from '../context/CompanyContext.jsx';
import Card from '../components/ui/Card.jsx';
import Pagination from '../components/ui/Pagination.jsx';
import { usePagination } from '../hooks/usePagination.js';
import './ReceiptList.css';

/**
 * ReceiptList component - displays list of receipts with filters
 */
export default function ReceiptList() {
  const { user } = useContext(AuthContext);
  const { settings } = useOrganizationSettings();
  const { companyId } = useCompany();
  const [limit] = useState(30);
  const [total, setTotal] = useState(0);
  const [beats, setBeats] = useState([]);
  const [userList, setUserList] = useState([]);
  const [selectedBeat, setSelectedBeat] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserLabel, setSelectedUserLabel] = useState(null);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [rows, setRows] = useState([]);
  const [amountData, setAmountData] = useState({});
  const [loading, setLoading] = useState(true);

  const pagination = usePagination(total, limit);

  // Fetch all beats on mount
  useEffect(() => {
    getBeatsApi()
      .then(res => setBeats(res.data?.data || res.data || []))
      .catch(() => setBeats([]));
  }, []);
  
 // Fetch all users on mount (only if userWise is enabled and user is owner)
  useEffect(() => {
    if (settings.userWise && user?.isOwner) {
      getUserListApi()
        .then(res => {
          const users = res.data?.data || res.data || [];
          setUserList(users);
          
          // Set default selection to logged-in user for owners
          if (user?.tallyUserId && users.length > 0) {
            const defaultUser = users.find(sp => sp.tallyUserId === user.tallyUserId);
            if (defaultUser) {
              setSelectedUserId(defaultUser.tallyUserId);
              setSelectedUserLabel(`${defaultUser.userName} - ${defaultUser.tallyUserId}`);
            }
          }
        })
        .catch(() => setUserList([]));
    }
  }, [settings.userWise, user?.isOwner, user?.tallyUserId]);


  useEffect(() => {
    setLoading(true);
    const params = {
      ...(settings.beatWise && selectedBeat ? { beatName: selectedBeat } : {}),
      // For userWise: owners can select, non-owners auto-filter by logged-in user
      ...(settings.userWise ? {
        tallyUserId: user?.isOwner ? (selectedUserId || user?.tallyUserId) : user?.tallyUserId
      } : {}),
      // Company filtering (Case 3 & 4) - use global company from context
      ...(settings.userWise && companyId ? { companyId: companyId } : {}),
      ...(startDate && endDate
        ? {
            startDate: startDate.toISOString().slice(0, 10),
            endDate: endDate.toISOString().slice(0, 10),
          }
        : {}),
      page: pagination.currentPage,
      limit,
    };
    getReceiptApi(params)
      .then((res) => {
        const data = res.data?.data || res.data || [];
        setRows(data.rows || data);
        setAmountData(res.data.amountData || {});
        setTotal(res.data.totalCount || 0);
      })
      .catch(() => {
        setRows([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [selectedBeat, selectedUserId, companyId, startDate, endDate, pagination.currentPage, limit, settings.beatWise, settings.userWise, user?.isOwner, user?.tallyUserId]);

  const handleBeatChange = (selectedOption) => {
    setSelectedBeat(selectedOption ? selectedOption.value : '');
    pagination.resetPagination();
  };

  const handleUserChange = (selectedOption) => {
    // Only update if a selection was actually made (not when clicking outside)
    console.log(selectedOption); 
    console.log(selectedUserId);
    console.log(selectedUserLabel);
    if (selectedOption === null) {
      // Only clear if explicitly cleared (X button), not on outside click
      setSelectedUserId(null);
      setSelectedUserLabel(null);
    } else if (selectedOption && selectedOption.value) {
      setSelectedUserId(selectedOption.value);
      setSelectedUserLabel(selectedOption.label);
    }
    pagination.resetPagination();
  };

  const handleStartDateChange = (date) => {
    if (date) {
      const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
      setStartDate(utcDate);
      pagination.resetPagination();
    } else {
      setStartDate(new Date());
    }
  };

  const handleEndDateChange = (date) => {
    if (date) {
      const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
      setEndDate(utcDate);
      pagination.resetPagination();
    } else {
      setEndDate(new Date());
    }
  };
  
  return (
    <Card title="Receipt List" className="receipt-list-card">
      {/* Filters */}
      <div className="receipt-list-filters">
        <div className="receipt-list-filter-group">
          {settings.userWise && user?.isOwner && (
            <div className="receipt-list-filter-item">
              <label className="receipt-list-filter-label">User</label>
              <Select
                options={userList.map((sp) => ({
                  value: sp.tallyUserId,
                  label: `${sp.userName} - ${sp.tallyUserId}`,
                }))}
                value={
                  selectedUserId && selectedUserLabel
                    ? userList.find(u => u.tallyUserId === selectedUserId)
                      ? { value: selectedUserId, label: selectedUserLabel }
                      : null
                    : null
                }
                onChange={handleUserChange}
                // onBlur={() => {}} // Prevent clearing on blur
                placeholder="Select User"
                isClearable
                isSearchable
                className="react-select-container"
                classNamePrefix="react-select"
                menuIsOpen={undefined} // Let React Select control menu state
              />
            </div>
          )}

          {settings.beatWise && (
            <div className="receipt-list-filter-item">
              <label className="receipt-list-filter-label">Beat</label>
              <Select
                options={beats.map((b) => ({ value: b.beatName, label: b.beatName }))}
                value={selectedBeat ? { value: selectedBeat, label: selectedBeat } : null}
                onChange={handleBeatChange}
                placeholder="Select Beat"
                isClearable
                isSearchable
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
          )}

          <div className="receipt-list-filter-item">
            <label className="receipt-list-filter-label">Start Date</label>
            <ReactDatePicker
              selected={startDate}
              onChange={handleStartDateChange}
              dateFormat="dd-MM-yyyy"
              className="receipt-list-datepicker"
              placeholderText="Select start date"
              maxDate={endDate}
            />
          </div>

          <div className="receipt-list-filter-item">
            <label className="receipt-list-filter-label">End Date</label>
            <ReactDatePicker
              selected={endDate}
              onChange={handleEndDateChange}
              className="receipt-list-datepicker"
              dateFormat="dd-MM-yyyy"
              maxDate={new Date()}
              minDate={startDate}
              placeholderText="Select end date"
            />
          </div>
        </div>
      </div>

      {/* Summary Badges */}
      <div className="receipt-list-summary">
        <div className="receipt-list-badge receipt-list-badge-info">
          <span className="receipt-list-badge-label">Cash:</span>
          <span className="receipt-list-badge-value">₹{amountData.totalCash || 0}</span>
        </div>
        <div className="receipt-list-badge receipt-list-badge-primary">
          <span className="receipt-list-badge-label">UPI:</span>
          <span className="receipt-list-badge-value">₹{amountData.totalUpi || 0}</span>
        </div>
        <div className="receipt-list-badge receipt-list-badge-warning">
          <span className="receipt-list-badge-label">Cheque:</span>
          <span className="receipt-list-badge-value">₹{amountData.totalCheque || 0}</span>
        </div>
      </div>
      {/* <div
        className="d-flex align-items-center my-2 justify-content-between flex-wrap flex-md-nowrap"
        style={{
          width: '100%',
          gap: '0.5rem'
        }}
      >
        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center" style={{ gap: '0.5rem' }}>
          <label className="d-flex align-items-center" style={{ minWidth: '180px', gap: '0.5rem' }}>
            Beat:&nbsp;
            <select className="filter-select form-select form-select-sm" value={selectedBeat} onChange={handleBeatChange}>
              <option value=''>--Select Beat--</option>
              {beats.map(beat => (
                <option value={beat.beatName} key={beat.beatName}>
                  {beat.beatName}
                </option>
              ))}
            </select>
          </label>
          
        </div>
      </div> */}
          {/* Table */}
      {loading ? (
        <div className="receipt-list-loading">Loading...</div>
      ) : (
        <>
          <div className="table-responsive-wrapper">
            <table className="receipt-list-table table-responsive">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Receipt Date</th>
                  <th>Receipt Number</th>
                  <th>Customer</th>
                  <th>User</th>
                  <th>Cash</th>
                  <th>UPI</th>
                  <th>Cheque</th>
                  <th>Advance</th>
                  { settings.billWise && (
                    <th>Allocations</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="receipt-list-empty">
                      No records available
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => (
                    <tr key={r.id}>
                      <td>{(pagination.currentPage - 1) * limit + idx + 1}</td>
                      <td>{r.receiptDate || '-'}</td>
                      <td>{r.receiptNumber || '-'}</td>
                      <td>{r.customerName || '-'}</td>
                      <td>{r.userName || '-'}</td>
                      <td>{r.cashAmount ?? '-'}</td>
                      <td>{r.upiAmount ?? '-'}</td>
                      <td>{r.chequeAmount ?? '-'}</td>
                      <td>{r.advanceAmount ?? '-'}</td>
                      { settings.billWise && (
                        <>
                      <td>
                        {r.ReceiptAllocations && r.ReceiptAllocations.length > 0 ? (
                          <ul className="receipt-list-allocations">
                            {r.ReceiptAllocations.map((alloc, i) => (
                              <li key={i}>
                                {alloc.billNumber}: ₹{alloc.billPaidAmount}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          '-'
                        )}
                      </td>
                      </>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && total > 0 && (
            <div className="receipt-list-pagination">
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={pagination.goToPage}
              />
            </div>
          )}
        </>
      )}
    </Card>
  );
}
